// Comprehensive Arizona family law data based on FindLaw and official Arizona sources
const arizonaData = {
  'arizona': {
    name: 'Arizona',
    overview: {
      keyPoints: [
        'Arizona uses the term "legal decision-making" instead of "legal custody" and "parenting time" instead of "physical custody"',
        'The state determines custody based on the "best interests of the child" standard outlined in A.R.S. § 25-403',
        'Arizona courts presume that joint legal decision-making is in the child\'s best interest unless there is evidence of domestic violence or child abuse',
        'Parenting plans are mandatory in all contested custody cases',
        'The court considers the past, present, and potential future relationship between the parent and the child',
        'Arizona law requires parents to attend a parent information program in all cases involving minor children',
        'Relocation of a child more than 100 miles or out of state requires 60 days written notice to the other parent',
        'Grandparents and third parties may petition for visitation rights under specific circumstances'
      ],
      uniqueFeatures: [
        'Arizona has specific statutes regarding "in loco parentis" custody for non-parents who have acted as parents',
        'The state has a "Parenting Coordinator" program to help resolve ongoing disputes in high-conflict cases',
        'Arizona offers a "Conciliation Court" for marriage counseling and mediation services',
        'The state has specific guidelines for long-distance parenting plans',
        'Arizona law allows for the modification of legal decision-making or parenting time one year after the initial order, unless the child is in danger'
      ]
    },
    legalRequirements: {
      residency: 'The child must have lived in Arizona for at least 6 months before filing (or since birth if under 6 months)',
      filing: 'File a Petition for Legal Decision-Making, Parenting Time, and Child Support in the Superior Court of the county where the child resides',
      paternity: 'Unmarried fathers must establish paternity through voluntary acknowledgment or court order to have legal rights',
      documentation: 'Required documents include the Petition, Summons, Preliminary Injunction, and Parenting Plan',
      uniqueRules: [
        'Mandatory Parent Information Program class for both parents',
        'Mandatory mediation for contested custody issues before a trial can be set',
        'A Preliminary Injunction is automatically issued upon filing, preventing parents from removing the child from the state or hiding assets'
      ]
    },
    custodyTypes: {
      physicalCustody: 'Referred to as "Parenting Time" - the schedule of when the child is in each parent\'s physical care',
      legalCustody: 'Referred to as "Legal Decision-Making" - the authority to make major decisions regarding education, health care, and religious training',
      jointCustody: 'Joint Legal Decision-Making means both parents share decision-making authority; Joint Parenting Time means the child spends significant time with both parents',
      visitation: 'Term generally used for non-parent time (e.g., grandparent visitation); parents have "parenting time"',
      specialProvisions: [
        'Supervised parenting time may be ordered if a parent poses a risk to the child\'s safety',
        'Sole legal decision-making may be awarded if joint decision-making is not feasible or safe',
        'The court may appoint a Best Interest Attorney or Court Appointed Advisor for the child in complex cases'
      ]
    },
    parentingPlans: {
      required: true,
      elements: [
        'A specific schedule for parenting time, including weekdays, weekends, holidays, and vacations',
        'A designation of legal decision-making authority (sole or joint)',
        'A procedure for exchanges, including location and transportation',
        'A procedure for communication between parents and with the child',
        'A method for resolving future disputes (e.g., mediation)',
        'Provisions for periodic review of the plan'
      ],
      tips: [
        'Be detailed and specific to minimize future conflicts',
        'Consider the child\'s age and developmental needs when creating the schedule',
        'Include provisions for flexibility and makeup time',
        'Clearly define how holidays and school breaks will be shared',
        'Address how major decisions (education, medical) will be made if joint authority is granted'
      ]
    },
    childSupport: {
      guidelines: 'Arizona uses an "Income Shares" model, calculating support based on the combined income of both parents',
      factors: [
        'Gross income of both parents',
        'Number of children',
        'Amount of parenting time (adjustments are made for the number of days the child spends with the paying parent)',
        'Cost of health insurance for the child',
        'Childcare costs',
        'Extraordinary expenses (e.g., private school, special needs)'
      ],
      calculation: 'Calculated using the Arizona Child Support Guidelines and calculator',
      modifications: 'Can be modified if there is a "substantial and continuing change of circumstances" (e.g., significant income change, change in parenting time)',
      enforcement: 'Enforced through the Arizona Division of Child Support Services (DCSS), including wage garnishment, tax refund intercept, and license suspension'
    },
    resources: {
      courts: [
        {
          name: 'Arizona Judicial Branch - Family Law',
          url: 'https://www.azcourts.gov/familylaw',
          description: 'Official state court website with forms, guidelines, and self-help resources'
        },
        {
          name: 'Maricopa County Superior Court - Family Department',
          url: 'https://superiorcourt.maricopa.gov/family/',
          description: 'Resources and forms for the state\'s largest county'
        },
        {
          name: 'Pima County Superior Court - Family Law',
          url: 'https://www.sc.pima.gov/judges-courts/family-law/',
          description: 'Resources for Pima County residents'
        },
        {
          name: 'Arizona Child Support Calculator',
          url: 'https://www.azcourts.gov/familylaw/Child-Support-Calculator-Information',
          description: 'Official tool for estimating child support obligations'
        }
      ],
      support: [
        {
          name: 'Arizona Division of Child Support Services (DCSS)',
          url: 'https://des.az.gov/services/child-and-family/child-support-services',
          description: 'State agency for establishing and enforcing child support orders'
        },
        {
          name: 'Community Legal Services',
          url: 'https://clsaz.org/',
          description: 'Provides legal assistance to low-income Arizonans'
        },
        {
          name: 'Arizona Coalition to End Sexual and Domestic Violence',
          url: 'https://www.acesdv.org/',
          description: 'Resources and support for victims of domestic violence'
        }
      ],
      legal: [
        {
          name: 'State Bar of Arizona - Find a Lawyer',
          url: 'https://azbar.org/for-the-public/legal-help-education/find-a-lawyer/',
          description: 'Directory of licensed attorneys in Arizona'
        },
        {
          name: 'AZLawHelp.org',
          url: 'https://www.azlawhelp.org/',
          description: 'Free legal information and resources for Arizona residents'
        },
        {
          name: 'Modest Means Project',
          url: 'https://azbar.org/for-the-public/legal-help-education/modest-means-project/',
          description: 'Legal assistance for individuals who do not qualify for free legal aid but cannot afford standard rates'
        }
      ],
      educational: [
        {
          name: 'Parent Information Program',
          url: 'https://www.azcourts.gov/familylaw/Parent-Information-Program',
          description: 'Mandatory class for parents involved in divorce or custody cases'
        },
        {
          name: 'Conciliation Services',
          url: 'https://superiorcourt.maricopa.gov/llrc/conciliation-services/',
          description: 'Mediation and counseling services offered by the court'
        },
        {
          name: 'Planning for Parenting Time: Arizona\'s Guide for Parents Living Apart',
          url: 'https://www.azcourts.gov/familylaw/Parenting-Time-Plan',
          description: 'Comprehensive guide to creating parenting plans in Arizona'
        }
      ]
    },
    bestInterestsFactors: [
      'The past, present, and potential future relationship between the parent and the child',
      'The interaction and interrelationship of the child with the parent or parents, the child\'s siblings, and any other person who may significantly affect the child\'s best interest',
      'The child\'s adjustment to home, school, and community',
      'If the child is of suitable age and maturity, the wishes of the child as to legal decision-making and parenting time',
      'The mental and physical health of all individuals involved',
      'Which parent is more likely to allow the child frequent, meaningful, and continuing contact with the other parent',
      'Whether one parent intentionally misled the court to cause an unnecessary delay, to increase the cost of litigation, or to persuade the court to give a legal decision-making or a parenting time preference to that parent',
      'Whether there has been domestic violence or child abuse pursuant to section 25-403.03',
      'The nature and extent of coercion or duress used by a parent in obtaining an agreement regarding legal decision-making or parenting time',
      'Whether a parent has complied with chapter 3, article 5 of this title (regarding domestic relations education)',
      'Whether either parent was convicted of an act of false reporting of child abuse or neglect under section 13-2907.02'
    ]
  }
};

export default arizonaData;